import React, { useCallback, useEffect, useMemo, useState } from "react";
import Model, { Providers } from "@tripian/model";
import moment from "moment";
import { PreLoading, SvgIcons } from "@tripian/react";
import useLivnApi from "../../../hooks/useLivnApi";
import useReservation from "../../../hooks/useReservation";
import { displayLivnLineItemTotal, displayLivnPrice, getLivnMarkupPerc } from "../../../utils/livnPricing";
import {
  livnFollowUpGroups,
  livnQuestionsWithFollowUps,
  mapLivnFlowError,
  validateLivnAnswer,
} from "../../../utils/livnValidation";
import LivnQuestionInput from "./LivnQuestionInput";
import LivnPaymentStep, { LivnPaymentLine } from "./LivnPaymentStep";
import LivnBookingTickets from "./LivnBookingTickets";

interface ILivnTourInfo {
  tourId: number;
  startDate?: string;
  endDate?: string;
  isOpen: boolean;
  onClose: () => void;
}

/** Livn labels generic question steps via stepName and milestones via both fields. */
const STEP_LABELS: Record<string, string> = {
  PAX_COUNT: "Passenger Count",
  FARE_SELECTION: "Select Tickets",
  PICKUP_DROPOFF: "Pickup & Drop-off",
  PASSENGER_DETAILS: "Passenger Details",
  FINAL_QUOTE: "Review Details",
  ADDON_ASSIGNMENT: "Assign Extras",
  TEMPORARY_HOLD: "Confirm Booking",
  CONFIRMED_BOOKING: "Booking Confirmed",
};

const PAX_COUNT_STEP = "PAX_COUNT";

/** Details of the fare step, kept after the flow moves on (later steps drop fareDetails). */
type FareSnapshot = {
  paxCount: number;
  variantName?: string;
  timeSlotName?: string;
  timeStart?: string;
  timeEnd?: string;
};

/**
 * Livn returns 0 on `unitsMax` for fares/add-ons that have no explicit cap, so only positive
 * values are honoured as ceilings.
 */
const effectiveCap = (...values: Array<number | undefined>): number | undefined => {
  const caps = values.filter((v): v is number => v !== undefined && v > 0);
  return caps.length > 0 ? Math.min(...caps) : undefined;
};

const timeRange = (start?: string, end?: string): string | undefined => {
  const s = start?.slice(0, 5);
  const e = end?.slice(0, 5);
  if (!s) return undefined;
  return e ? `${s}–${e}` : s;
};

/** Livn reports duration in minutes — multi-day products come through as e.g. 2880. */
const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} ${days === 1 ? "day" : "days"}`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins === 0 ? `${hours} ${hours === 1 ? "hour" : "hours"}` : `${hours}h ${mins}m`;
};

const stepLabel = (step?: Providers.Livn.FlowStep): string => {
  const key = step?.stepName ?? step?.milestone ?? "";
  return STEP_LABELS[key] ?? key.replace(/_/g, " ").toLowerCase() ?? "Booking Step";
};

const LivnTourInfo: React.FC<ILivnTourInfo> = ({ tourId, startDate, endDate, isOpen, onClose }) => {
  const {
    fetchLivnProductInfo,
    fetchLivnDepartures,
    startLivnFlow,
    fetchLivnPricePreview,
    fetchLivnBookings,
    continueLivnFlow,
    goBackLivnFlow,
    loadingLivnProductInfo,
    loadingLivnDepartures,
    loadingLivnFlow,
    loadingLivnPricePreview,
    loadingLivnBookings,
    livnProductInfo,
    livnDepartures,
    livnFlow,
    livnPricePreview,
    livnBookings,
    setLivnBookings,
    setLivnPricePreview,
  } = useLivnApi();
  const { reservationAdd } = useReservation();

  // Date is chosen first, then explicitly confirmed — starting the checkout flow (which holds
  // inventory and starts an expiry timer) must not happen on a stray tap of a date chip.
  const [pendingDate, setPendingDate] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");
  const [selectedVariantIdx, setSelectedVariantIdx] = useState<number>(0);
  const [selectedTimeSlotIdx, setSelectedTimeSlotIdx] = useState<number>(0);
  const [fareSelections, setFareSelections] = useState<Record<string, number>>({});
  const [addOnSelections, setAddOnSelections] = useState<Record<string, number>>({});
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>({});
  const [touchedAnswers, setTouchedAnswers] = useState<Record<string, boolean>>({});
  const [showAllAnswerErrors, setShowAllAnswerErrors] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingError, setBookingError] = useState<string>("");
  const [bookingNotice, setBookingNotice] = useState<string>("");
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentAuthorized, setPaymentAuthorized] = useState(false);
  const [fareSnapshot, setFareSnapshot] = useState<FareSnapshot>();
  const [passengerSnapshot, setPassengerSnapshot] = useState<string[]>([]);
  const [quoteSnapshot, setQuoteSnapshot] = useState<Providers.Livn.FinalQuote>();
  /**
   * Passenger count declared on the PAX_COUNT step. Livn then requires the fare selection to add
   * up to exactly this many travellers, and rejects the whole step otherwise.
   */
  const [declaredPaxCount, setDeclaredPaxCount] = useState<number>();

  const today = moment().format("YYYY-MM-DD");
  const searchStart = startDate && moment(startDate).isAfter(today) ? startDate : today;
  const searchEnd =
    endDate && moment(endDate).isAfter(searchStart)
      ? endDate
      : moment(searchStart).add(30, "days").format("YYYY-MM-DD");

  // ── Derived from active flow step ──
  // Also show FAILED steps that can be retried (terminateTransaction !== true) so the form stays
  // visible with the customer's answers intact.
  const activeStep = livnFlow?.steps.find(
    s => s.status === "ACTIVE" || (s.status === "FAILED" && !s.error?.terminateTransaction)
  );
  const baseVariants = activeStep?.fareDetails?.baseVariants ?? [];
  const selectedVariant = baseVariants[selectedVariantIdx] ?? baseVariants[0];
  const timeSlots = selectedVariant?.timeSlots ?? [];
  const selectedTimeSlot = timeSlots[selectedTimeSlotIdx] ?? timeSlots[0];

  const currentFares = useMemo(() => selectedTimeSlot?.fares ?? [], [selectedTimeSlot]);
  const variantAddOns = useMemo(() => selectedVariant?.addOns ?? [], [selectedVariant]);
  const timeSlotAddOns = useMemo(() => selectedTimeSlot?.addOns ?? [], [selectedTimeSlot]);

  // Livn expands per-passenger questions server-side into one group per traveller
  // ("Adult - Participant 1", …) and always reports scope PER_GROUP, so the groups — not the
  // scope field — are what tells a customer which answers belong to whom.
  const questionGroups = useMemo(() => activeStep?.questions?.questionGroups ?? [], [activeStep]);

  /**
   * Görünen tüm sorular: adımın kendi soruları + seçilen seçeneklerin ortaya çıkardığı
   * takip soruları. Livn koşullu soruyu ayrı bir adım olarak göndermeyip seçeneğin
   * içine gömdüğü için (ör. frequent flyer üyeliği → 10 haneli numara), doğrulama ve
   * gönderilen cevaplar bu listeden türetilmek zorunda; yoksa zorunlu bir soru hiç
   * sorulmadan adım gönderilir.
   */
  const allQuestions = useMemo(
    () =>
      livnQuestionsWithFollowUps(
        questionGroups.flatMap(g => g.questions),
        questionAnswers
      ),
    [questionGroups, questionAnswers]
  );

  const totalPax = useMemo(
    () =>
      Object.entries(fareSelections).reduce((sum, [uuid, qty]) => {
        const fare = currentFares.find(f => f.uuid === uuid);
        return sum + qty * (fare?.paxMin ?? 1);
      }, 0),
    [fareSelections, currentFares]
  );

  /**
   * A linked add-on attached to a variant or timeslot covers the whole party: Livn requires its
   * quantity to equal the total booking size exactly and rejects anything partial
   * ("'…' quantity (1) must be equal to the total booking size (3)"), so it is offered as a
   * single on/off choice rather than a stepper.
   */
  const isWholeBookingAddOn = (ao: Providers.Livn.AddOn, linkedFareUuid?: string): boolean =>
    ao.unitsLinkedToParent === true && !linkedFareUuid;

  /**
   * Ceiling for a stepper add-on: a fare-level linked one is bounded by that fare's selected
   * units (a partial quantity is allowed — Livn then adds an ADDON_ASSIGNMENT step to ask which
   * traveller each unit is for), an independent one by unitsMax.
   */
  const addOnCap = useCallback(
    (ao: Providers.Livn.AddOn, linkedFareUuid?: string): number | undefined => {
      if (ao.unitsLinkedToParent) {
        return linkedFareUuid ? fareSelections[linkedFareUuid] ?? 0 : totalPax;
      }
      return effectiveCap(ao.unitsMax);
    },
    [fareSelections, totalPax]
  );

  /**
   * Effective quantity: whole-booking add-ons always resolve to the current party size (so they
   * follow ticket changes), and stepper add-ons are clamped to their cap so shrinking the parent
   * can't leave a stale quantity behind.
   */
  const addOnQty = useCallback(
    (ao: Providers.Livn.AddOn, linkedFareUuid?: string): number => {
      const selected = addOnSelections[ao.uuid] ?? 0;
      if (isWholeBookingAddOn(ao, linkedFareUuid)) return selected > 0 ? totalPax : 0;
      const cap = addOnCap(ao, linkedFareUuid);
      return cap === undefined ? selected : Math.min(selected, cap);
    },
    [addOnSelections, addOnCap, totalPax]
  );

  // ── Answer validation ──
  const answerErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    allQuestions.forEach(q => {
      const error = validateLivnAnswer(q, questionAnswers[q.uuid] ?? "");
      if (error) errs[q.uuid] = error;
    });
    return errs;
  }, [allQuestions, questionAnswers]);

  const answersValid = Object.keys(answerErrors).length === 0;
  const visibleError = (uuid: string) =>
    showAllAnswerErrors || touchedAnswers[uuid] ? answerErrors[uuid] : undefined;

  // Reset step state when the active step changes, seeding any supplier-provided defaults.
  // Keeps bookingError so the customer can still see why the form moved.
  useEffect(() => {
    if (!activeStep) return;
    const defaults: Record<string, string> = {};
    (activeStep.questions?.questionGroups ?? []).forEach(g =>
      g.questions.forEach(q => {
        if (q.defaultValue) defaults[q.uuid] = q.defaultValue;
      })
    );
    setSelectedVariantIdx(0);
    setSelectedTimeSlotIdx(0);
    setFareSelections({});
    setAddOnSelections({});
    setQuestionAnswers(defaults);
    setTouchedAnswers({});
    setShowAllAnswerErrors(false);
  }, [activeStep?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown timer — `expires` is the populated field, `expiresAt` is always null in the API
  const stepExpiry = activeStep?.expires ?? activeStep?.expiresAt;
  useEffect(() => {
    if (!stepExpiry) {
      setTimeLeft(null);
      return undefined;
    }
    const tick = () => {
      const remaining = Math.max(0, new Date(stepExpiry).getTime() - Date.now());
      setTimeLeft(Math.floor(remaining / 1000));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [stepExpiry]);

  useEffect(() => {
    if (isOpen) {
      void fetchLivnProductInfo(tourId);
      void fetchLivnDepartures(tourId, searchStart, searchEnd);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tourId]);

  // ── Pricing shown in the summary and charged at the payment step ──
  // The confirming step is not always the one that carried the quote (e.g. TEMPORARY_HOLD follows
  // FINAL_QUOTE), so the last quote seen is retained for the payment screen.
  const livePrice = livnPricePreview?.quote ?? activeStep?.finalQuote;
  const priceData: Providers.Livn.FinalQuote | undefined = livePrice ?? quoteSnapshot;
  const markupPerc = getLivnMarkupPerc(livnProductInfo);
  const paymentLines: LivnPaymentLine[] = useMemo(() => {
    if (!priceData) return [];
    return priceData.lineItems.map(item => {
      const displayed = displayLivnLineItemTotal(item, markupPerc);
      return { title: item.title, quantity: item.quantity, amount: displayed.amount, currency: displayed.currency };
    });
  }, [priceData, markupPerc]);
  const paymentTotal = useMemo(
    () => Math.round(paymentLines.reduce((sum, l) => sum + l.amount, 0) * 100) / 100,
    [paymentLines]
  );
  const paymentCurrency = paymentLines[0]?.currency ?? priceData?.grossTotal.currency ?? livnFlow?.currency ?? "";

  // Payment is authorised before the booking is confirmed, so a failure at the confirmation step
  // means an authorisation to release — not "we never charged you".
  const paymentReleasedNotice =
    paymentTotal > 0
      ? `Your booking could not be confirmed, so the ${paymentTotal} ${paymentCurrency} payment authorisation has been released. You have not been charged — any pending amount will clear on your statement within a few business days.`
      : "Your booking could not be confirmed and no payment has been taken.";

  const paxCount = fareSnapshot?.paxCount ?? (totalPax > 0 ? totalPax : declaredPaxCount) ?? 0;

  /** Keeps context that later flow steps no longer return, for the payment screen and tickets. */
  const captureSnapshots = useCallback(() => {
    if (livePrice) setQuoteSnapshot(livePrice);

    if (activeStep?.fareDetails && totalPax > 0) {
      setFareSnapshot({
        paxCount: totalPax,
        variantName: selectedVariant?.name,
        timeSlotName: selectedTimeSlot?.name,
        timeStart: selectedTimeSlot?.timeStart,
        timeEnd: selectedTimeSlot?.timeEnd,
      });
    }

    // Participant groups carry the traveller names; the supplier does not always echo them back.
    const names = questionGroups
      .filter(g => /participant/i.test(g.caption ?? ""))
      .map(g => {
        const first = g.questions.find(q => q.purpose === "PAX_FIRST_NAME");
        const last = g.questions.find(q => q.purpose === "PAX_LAST_NAME");
        return [first ? questionAnswers[first.uuid] : "", last ? questionAnswers[last.uuid] : ""]
          .filter(Boolean)
          .join(" ")
          .trim();
      })
      .filter(Boolean);
    if (names.length > 0) setPassengerSnapshot(names);
  }, [
    livePrice,
    activeStep,
    totalPax,
    selectedVariant,
    selectedTimeSlot,
    questionGroups,
    questionAnswers,
  ]);

  const handleDateClick = useCallback((date: string) => {
    // Only stage the date — the flow is created once the customer confirms below.
    setPendingDate(date);
    setSelectedDate("");
    setBookingConfirmed(false);
    setBookingError("");
    setBookingNotice("");
    setShowPayment(false);
    setPaymentAuthorized(false);
    setFareSnapshot(undefined);
    setPassengerSnapshot([]);
    setQuoteSnapshot(undefined);
    setDeclaredPaxCount(undefined);
  }, []);

  const handleConfirmDate = useCallback(async () => {
    if (!pendingDate) return;
    setSelectedDate(pendingDate);
    setBookingError("");
    await startLivnFlow(tourId, pendingDate, selectedCurrency || undefined);
  }, [pendingDate, tourId, selectedCurrency, startLivnFlow]);

  /**
   * Any change to what is being bought invalidates the quoted price. The quote is what the
   * payment step charges, so a stale preview must never survive a selection change.
   */
  const invalidateQuote = useCallback(() => {
    setLivnPricePreview(undefined);
    setShowPayment(false);
  }, [setLivnPricePreview]);

  const handleFareChange = (uuid: string, delta: number, fare?: Providers.Livn.Fare) => {
    invalidateQuote();
    // unitsMultipleOf fares (e.g. a twin room sold in pairs) must only ever land on 0 or a
    // multiple of that step — Livn fails the step for any other quantity.
    const step = fare?.unitsMultipleOf && fare.unitsMultipleOf > 1 ? fare.unitsMultipleOf : 1;
    const min = fare?.unitsMin && fare.unitsMin > step ? fare.unitsMin : step;
    const max = effectiveCap(fare?.unitsMax, fare?.unitsAvailable);
    setFareSelections(prev => {
      const current = prev[uuid] ?? 0;
      if (delta > 0) {
        const next = current === 0 ? min : current + step;
        return { ...prev, [uuid]: max !== undefined && next > max ? current : next };
      }
      const next = current - step;
      return { ...prev, [uuid]: next <= 0 || next < min ? 0 : next };
    });
  };

  /** Whole-booking add-ons are all-or-nothing; addOnQty resolves the stored flag to the party size. */
  const handleWholeBookingAddOnToggle = (ao: Providers.Livn.AddOn, checked: boolean) => {
    invalidateQuote();
    setAddOnSelections(prev => ({ ...prev, [ao.uuid]: checked ? 1 : 0 }));
  };

  const handleAddOnChange = (ao: Providers.Livn.AddOn, delta: number, linkedFareUuid?: string) => {
    invalidateQuote();
    const min = ao.unitsMin && ao.unitsMin > 0 ? ao.unitsMin : 1;
    const cap = addOnCap(ao, linkedFareUuid);
    setAddOnSelections(prev => {
      const current = Math.min(prev[ao.uuid] ?? 0, cap ?? Number.MAX_SAFE_INTEGER);
      if (delta > 0) {
        const next = current === 0 ? min : current + 1;
        return { ...prev, [ao.uuid]: cap !== undefined && next > cap ? current : next };
      }
      const next = current - 1;
      return { ...prev, [ao.uuid]: next < min ? 0 : next };
    });
  };

  const buildStepRequest = useCallback((): Providers.Livn.FlowStepRequest | null => {
    if (!activeStep) return null;

    const fareSelectionsArr = Object.entries(fareSelections)
      .filter(([, qty]) => qty > 0)
      .map(([uuid, quantity]) => ({ uuid, quantity }));

    // Linked add-ons ARE customer-selected — their ceiling is just the parent's quantity. Each is
    // clamped to that ceiling so a selection made before the parent shrank is never submitted.
    const allAddOns: Array<{ ao: Providers.Livn.AddOn; linkedFareUuid?: string }> = [
      ...variantAddOns.map(ao => ({ ao, linkedFareUuid: undefined })),
      ...timeSlotAddOns.map(ao => ({ ao, linkedFareUuid: undefined })),
      ...currentFares.flatMap(f => (f.addOns ?? []).map(ao => ({ ao, linkedFareUuid: f.uuid }))),
    ];
    const addOnSelectionsArr = allAddOns
      .map(({ ao, linkedFareUuid }) => ({ uuid: ao.uuid, quantity: addOnQty(ao, linkedFareUuid) }))
      .filter(sel => sel.quantity > 0);

    // Yalnızca şu an GÖRÜNEN sorular gönderilir. questionAnswers, kullanıcı seçimini
    // değiştirdiğinde artık gösterilmeyen takip sorularının cevabını da tutuyor olabilir;
    // adımın tanımadığı bir soruyu göndermek Livn'de "is not related to this step"
    // hatasına düşüyor.
    const answersArr = Object.entries(questionAnswers)
      .filter(([, value]) => Boolean(value))
      .flatMap(([uuid, value]) => {
        const question = allQuestions.find(q => q.uuid === uuid);
        if (!question) return [];
        if (question.answerType === "SELECT_MULTIPLE") {
          return value.split(",").filter(Boolean).map(v => ({ questionUuid: uuid, value: v }));
        }
        return [{ questionUuid: uuid, value }];
      });

    // Only include fareSelections/addOnSelections when this step actually presents fares, and
    // answers only when it presents questions — sending a key the step has no concept of makes
    // Livn's validation fail with a generic internal error.
    return {
      id: activeStep.id,
      ...(activeStep.fareDetails && { fareSelections: fareSelectionsArr, addOnSelections: addOnSelectionsArr }),
      ...(activeStep.questions && { answers: { answers: answersArr } }),
    };
  }, [
    activeStep,
    fareSelections,
    variantAddOns,
    timeSlotAddOns,
    currentFares,
    addOnQty,
    questionAnswers,
    allQuestions,
  ]);

  const hasFares = currentFares.length > 0;
  const hasTicketsSelected = Object.values(fareSelections).some(q => q > 0);
  const isLastStep = activeStep?.nextStepConfirmedBooking === true;

  // Livn requires the fare selection to add up to exactly the count declared on the PAX_COUNT step.
  const paxCountMismatch = declaredPaxCount !== undefined && hasFares && totalPax !== declaredPaxCount;
  const selectionComplete = Boolean(activeStep) && (hasFares ? hasTicketsSelected && !paxCountMismatch : true);
  const canSubmit = selectionComplete && answersValid;

  /** Remembers the declared traveller count before the PAX_COUNT step is submitted. */
  const capturePaxCount = useCallback(() => {
    if ((activeStep?.stepName ?? activeStep?.milestone) !== PAX_COUNT_STEP) return;
    const numeric = allQuestions.find(q => q.answerType === "INTEGER" || q.answerType === "NUMBER");
    const value = numeric ? Number(questionAnswers[numeric.uuid]) : NaN;
    if (Number.isFinite(value) && value > 0) setDeclaredPaxCount(value);
  }, [activeStep, allQuestions, questionAnswers]);

  const handleCheckPrice = useCallback(async () => {
    if (!livnFlow) return;
    if (!answersValid) {
      setShowAllAnswerErrors(true);
      return;
    }
    const step = buildStepRequest();
    if (!step) return;
    setBookingError("");
    const result = await fetchLivnPricePreview({ id: livnFlow.id, steps: [step] });
    if (!result) setBookingError("We couldn't calculate the price for this selection. Please adjust it and try again.");
  }, [livnFlow, answersValid, buildStepRequest, fetchLivnPricePreview]);

  const handleContinue = useCallback(async () => {
    if (!livnFlow) return;
    if (!answersValid) {
      setShowAllAnswerErrors(true);
      return;
    }
    const step = buildStepRequest();
    if (!step) return;
    setBookingError("");
    setBookingNotice("");
    capturePaxCount();
    captureSnapshots();

    const result = await continueLivnFlow({ id: livnFlow.id, steps: [step] });
    if (!result) {
      setBookingError("We couldn't proceed with this booking. Please review your selections and try again.");
      if (paymentAuthorized) setBookingNotice(paymentReleasedNotice);
      return;
    }
    const failedStep = result.steps.find(s => s.status === "FAILED");
    if (failedStep?.error) {
      // Livn's own customerErrorMessage carries internal detail — always re-express it.
      setBookingError(mapLivnFlowError(failedStep.error));
      if (failedStep.error.terminateTransaction) {
        setShowPayment(false);
        if (paymentAuthorized) {
          setBookingNotice(`${paymentReleasedNotice} Please start the booking again.`);
          setPaymentAuthorized(false);
        }
        if (selectedDate) {
          // Flow is dead — restart with the same date so the customer can try again
          await startLivnFlow(tourId, selectedDate, selectedCurrency || undefined);
        }
      }
      return;
    }
    const nextActive = result.steps.find(s => s.status === "ACTIVE");
    if (!nextActive) {
      setBookingConfirmed(true);
      setShowPayment(false);
      let bookings = result.bookings;
      if (bookings && bookings.length > 0) {
        setLivnBookings(bookings);
      } else if (result.livnReference) {
        bookings = (await fetchLivnBookings(result.livnReference)) ?? undefined;
      }

      const livnReference = result.livnReference ?? bookings?.[0]?.livnReference;
      if (livnReference) {
        const travelDate = bookings?.[0]?.tickets?.[0]?.travelDate;
        await reservationAdd(
          {
            key: livnReference,
            provider: Model.PROVIDER_NAME.LIVN,
            value: {
              livnReference,
              bookings: bookings ?? [],
              tourName: livnProductInfo?.name,
              tourImage: livnProductInfo?.images?.[0]?.url,
              // Stored so the ticket PDF regenerated from My Bookings shows the same
              // marked-up invoice the customer actually paid.
              markupPerc,
            },
            // Livn travel dates carry no time — store end of day so a same-day booking
            // doesn't immediately count as past in reservation lists.
            bookingDateTime: travelDate
              ? moment(travelDate).endOf("day").format("YYYY-MM-DD HH:mm:ss")
              : moment().format("YYYY-MM-DD HH:mm:ss"),
          },
          false
        );
      }
    }
  }, [
    livnFlow,
    answersValid,
    buildStepRequest,
    capturePaxCount,
    captureSnapshots,
    continueLivnFlow,
    paymentAuthorized,
    paymentReleasedNotice,
    selectedDate,
    selectedCurrency,
    tourId,
    startLivnFlow,
    fetchLivnBookings,
    setLivnBookings,
    reservationAdd,
    livnProductInfo,
    markupPerc,
  ]);

  const handleGoBack = useCallback(async () => {
    if (!livnFlow) return;
    setBookingError("");
    setBookingNotice("");
    await goBackLivnFlow(livnFlow.id);
  }, [livnFlow, goBackLivnFlow]);

  const handleProceedToPayment = () => {
    if (!answersValid) {
      setShowAllAnswerErrors(true);
      return;
    }
    capturePaxCount();
    captureSnapshots();
    setBookingError("");
    setShowPayment(true);
  };

  const handlePay = () => {
    setPaymentAuthorized(true);
    void handleContinue();
  };

  // ── Add-on row ──
  const renderAddOnRow = (ao: Providers.Livn.AddOn, linkedFareUuid?: string) => {
    const qty = addOnQty(ao, linkedFareUuid);
    const cap = addOnCap(ao, linkedFareUuid);
    const aoPrice = displayLivnPrice(ao.price, livnProductInfo);
    const wholeBooking = isWholeBookingAddOn(ao, linkedFareUuid);
    const perUnit = `+${aoPrice.amount} ${aoPrice.currency}`;
    const blockedReason =
      ao.unitsLinkedToParent && cap === 0
        ? linkedFareUuid
          ? "Select this ticket first"
          : "Select your tickets first"
        : undefined;

    return (
      <div key={ao.uuid} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg bg-gray-50">
        <div className="flex-1 min-w-0 mr-3">
          <span className="text-sm font-medium text-gray-700">{ao.name}</span>
          <span className="text-xs text-primary-color font-semibold ml-2">
            {perUnit}
            {ao.unitsLinkedToParent ? " per traveller" : ""}
          </span>
          {wholeBooking && totalPax > 0 && (
            <span className="text-xs text-gray-500 ml-2">
              — {Math.round(aoPrice.amount * totalPax * 100) / 100} {aoPrice.currency} for {totalPax}
            </span>
          )}
          {!wholeBooking && cap !== undefined && cap > 1 && (
            <span className="text-xs text-gray-400 ml-2">up to {cap}</span>
          )}
          {ao.description && <p className="text-xs text-gray-500 mt-0.5">{ao.description}</p>}
          {ao.specialNotes && <p className="text-xs text-gray-500 mt-0.5">{ao.specialNotes}</p>}
          {wholeBooking && <p className="text-xs text-gray-400 mt-0.5">Applies to everyone in the booking.</p>}
        </div>

        {blockedReason ? (
          <span className="text-xs text-gray-400 italic flex-shrink-0">{blockedReason}</span>
        ) : wholeBooking ? (
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              checked={qty > 0}
              onChange={e => handleWholeBookingAddOnToggle(ao, e.target.checked)}
              aria-label={ao.name}
            />
            Add
          </label>
        ) : (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-40"
              onClick={() => handleAddOnChange(ao, -1, linkedFareUuid)}
              disabled={qty === 0}
              aria-label={`Remove ${ao.name}`}
            >
              <span className="text-base leading-none">−</span>
            </button>
            <span className="w-5 text-center text-sm font-medium">{qty}</span>
            <button
              className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-40"
              onClick={() => handleAddOnChange(ao, 1, linkedFareUuid)}
              disabled={cap !== undefined && qty >= cap}
              aria-label={`Add ${ao.name}`}
            >
              <span className="text-base leading-none">+</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderQuestion = (q: Providers.Livn.Question) => {
    // Seçilen seçenek ek soru açıyorsa (ör. frequent flyer üyeliği → üyelik numarası)
    // hemen altında, girintili olarak gösterilir. Özyineleme: takip sorusu da seçim
    // sorusu olup kendi takibini açabilir.
    const followUpGroups = livnFollowUpGroups(q, questionAnswers[q.uuid]);

    return (
      <div key={q.uuid}>
        <label className="block text-sm text-gray-600 mb-1">
          {q.title}
          {q.required && <span className="text-red-500 ms-1">*</span>}
        </label>
        <LivnQuestionInput
          question={q}
          value={questionAnswers[q.uuid] ?? ""}
          error={visibleError(q.uuid)}
          onChange={value => setQuestionAnswers(prev => ({ ...prev, [q.uuid]: value }))}
          onBlur={() => setTouchedAnswers(prev => ({ ...prev, [q.uuid]: true }))}
        />

        {followUpGroups.length > 0 && (
          <div className="mt-2 ps-3 border-s-2 border-gray-200 space-y-2">
            {followUpGroups.map((group, gi) => (
              <div key={`${q.uuid}-followup-${gi}`}>
                {group.caption && (
                  <p className="text-xs font-medium text-gray-500 mb-1">{group.caption}</p>
                )}
                <div className="space-y-2">{group.questions.map(renderQuestion)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const formatTimeLeft = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // ── Itinerary — legs carry dayFrom/dayTo + timeFrom/timeTo ──
  const itineraryItems = useMemo(() => {
    const items = livnProductInfo?.itinerary?.items ?? [];
    return [...items].sort(
      (a, b) => (a.dayFrom ?? 0) - (b.dayFrom ?? 0) || (a.timeFrom ?? "").localeCompare(b.timeFrom ?? "")
    );
  }, [livnProductInfo]);

  const itineraryDayLabel = (item: Providers.Livn.ItineraryItem): string | undefined => {
    if (item.dayFrom === undefined) return undefined;
    const to = item.dayTo ?? item.dayFrom;
    return to > item.dayFrom ? `Days ${item.dayFrom}–${to}` : `Day ${item.dayFrom}`;
  };

  // `steps` only ever holds the steps reached so far, so the roadmap is the only real total.
  const roadmapLength = useMemo(
    () => (livnFlow?.roadmap ?? "").split(",").filter(s => s.trim()).length,
    [livnFlow]
  );

  // ═══════════════════════════════════════
  // Loading / empty states
  // ═══════════════════════════════════════
  if (loadingLivnProductInfo) {
    return (
      <div className="fixed inset-0 bg-white z-[1100] flex items-center justify-center">
        <PreLoading />
      </div>
    );
  }

  if (!livnProductInfo) {
    return <div className="mt-12 mx-auto p-4 text-center text-gray-500">Tour information could not be loaded.</div>;
  }

  const images = livnProductInfo.images ?? [];
  const coverImage = images[currentImageIndex]?.url;
  const startLocation = livnProductInfo.locationsStart?.[0];
  const endLocation = livnProductInfo.locationsEnd?.[0];
  // Livn only fills the street fields when streetAddressAccuracy is true — otherwise the
  // coordinates are a city-level approximation and a street line would mislead.
  const locationLabel = (loc?: Providers.Livn.Location) =>
    [
      loc?.streetAddressAccuracy ? loc?.address1 : undefined,
      loc?.district,
      loc?.city,
      loc?.state,
      loc?.postcode,
      loc?.country,
    ]
      .filter(Boolean)
      .join(", ");

  const currencies = livnProductInfo.currencies ?? [];
  const activeCurrency = livnFlow?.currency || selectedCurrency || currencies[0];
  /** fromPrices carries one entry per supported currency. */
  const priceInCurrency = (prices?: Providers.Livn.Price[]) =>
    prices?.find(p => p.currency === activeCurrency) ?? prices?.[0];

  const snapshotTimeLabel =
    timeRange(fareSnapshot?.timeStart, fareSnapshot?.timeEnd) ?? fareSnapshot?.timeSlotName ?? undefined;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[1100] overflow-y-auto">
      <div className="min-h-screen bg-white relative mt-[60px] md:mt-[90px]">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* ── Image carousel ── */}
          {coverImage && (
            <div className="relative h-72 rounded-xl overflow-hidden mb-6">
              <img src={coverImage} alt={livnProductInfo.name} className="w-full h-full object-cover" />
              {images.length > 1 && (
                <div className="absolute bottom-3 right-3 flex items-center gap-2">
                  <button
                    className="p-1.5 bg-white/80 rounded-full hover:bg-white"
                    onClick={() => setCurrentImageIndex(i => Math.max(0, i - 1))}
                    disabled={currentImageIndex === 0}
                  >
                    <SvgIcons.ArrowLeft className="w-4 h-4" />
                  </button>
                  <span className="px-2 py-0.5 bg-black/50 text-white text-xs rounded-full">
                    {currentImageIndex + 1}/{images.length}
                  </span>
                  <button
                    className="p-1.5 bg-white/80 rounded-full hover:bg-white"
                    onClick={() => setCurrentImageIndex(i => Math.min(images.length - 1, i + 1))}
                    disabled={currentImageIndex === images.length - 1}
                  >
                    <SvgIcons.ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              <button className="absolute top-3 left-3 p-2 bg-white/80 rounded-full hover:bg-white" onClick={onClose}>
                <SvgIcons.ArrowLeft className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* ── Title & meta ── */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{livnProductInfo.name}</h1>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
              {livnProductInfo.duration && <span>⏱ {formatDuration(livnProductInfo.duration)}</span>}
              {livnProductInfo.ageMin !== undefined && <span>👤 Age {livnProductInfo.ageMin}+</span>}
              {livnProductInfo.groupSizeMax && <span>👥 Max {livnProductInfo.groupSizeMax} people</span>}
              {livnProductInfo.timeStart && (
                <span>
                  🕐 {livnProductInfo.timeStart.slice(0, 5)}
                  {livnProductInfo.timeStartRangeMax && `–${livnProductInfo.timeStartRangeMax.slice(0, 5)}`}
                </span>
              )}
              {livnProductInfo.operatingDaysStr && <span>📅 {livnProductInfo.operatingDaysStr}</span>}
              {startLocation && locationLabel(startLocation) && <span>📍 {locationLabel(startLocation)}</span>}
              {(() => {
                const price = priceInCurrency(livnProductInfo.fromPrices);
                if (!price) return null;
                const displayed = displayLivnPrice(price, livnProductInfo);
                return (
                  <span className="font-semibold text-primary-color">
                    From {displayed.amount} {displayed.currency}
                  </span>
                );
              })()}
            </div>
            {livnProductInfo.supplier && <p className="text-sm text-gray-400 mt-1">by {livnProductInfo.supplier.name}</p>}
            {livnProductInfo.operatingSchedule && (
              <p className="text-sm text-gray-500 mt-1">{livnProductInfo.operatingSchedule}</p>
            )}
          </div>

          {/* ── Description ── */}
          {livnProductInfo.description && (
            <div className="mb-5">
              <h2 className="text-lg font-semibold mb-2">About this activity</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line text-sm">{livnProductInfo.description}</p>
            </div>
          )}

          {/* ── Highlights ── */}
          {livnProductInfo.highlights?.highlights?.length ? (
            <div className="mb-5">
              <h2 className="text-lg font-semibold mb-2">Highlights</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {livnProductInfo.highlights.highlights.map((h, i) => (
                  <li key={i} className="flex gap-2 text-sm text-gray-600">
                    <span className="text-primary-color flex-shrink-0 mt-0.5">✦</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* ── Itinerary ── */}
          {(itineraryItems.length > 0 || startLocation || endLocation) && (
            <div className="mb-5">
              <h2 className="text-lg font-semibold mb-2">Itinerary</h2>

              {(startLocation || endLocation || livnProductInfo.timeStart || livnProductInfo.duration) && (
                <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {startLocation && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Starts</p>
                      <p className="text-gray-700">{locationLabel(startLocation) || "See meeting point details"}</p>
                      {livnProductInfo.timeStart && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {timeRange(livnProductInfo.timeStart, livnProductInfo.timeStartRangeMax)}
                        </p>
                      )}
                    </div>
                  )}
                  {(endLocation || livnProductInfo.duration) && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Ends</p>
                      <p className="text-gray-700">
                        {locationLabel(endLocation) || locationLabel(startLocation) || "Returns to the starting point"}
                      </p>
                      {livnProductInfo.duration && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatDuration(livnProductInfo.duration)} after start
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {itineraryItems.length > 0 && (
                <ol className="space-y-3">
                  {itineraryItems.map((item, i) => {
                    const dayLabel = itineraryDayLabel(item);
                    return (
                      <li key={i} className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-color/10 text-primary-color text-xs font-semibold flex items-center justify-center mt-0.5">
                          {item.dayFrom ?? i + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800">{item.title ?? `Stop ${i + 1}`}</p>
                          <div className="flex flex-wrap gap-x-3 text-xs text-gray-500">
                            {dayLabel && <span className="font-medium">{dayLabel}</span>}
                            {timeRange(item.timeFrom, item.timeTo) && <span>{timeRange(item.timeFrom, item.timeTo)}</span>}
                          </div>
                          {item.body && <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-line">{item.body}</p>}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}

          {/* ── Inclusions & Exclusions ── */}
          {livnProductInfo.inclusions?.items?.length || livnProductInfo.exclusions?.items?.length ? (
            <div
              className={`mb-5 grid gap-4 ${
                livnProductInfo.inclusions?.items?.length && livnProductInfo.exclusions?.items?.length
                  ? "grid-cols-1 sm:grid-cols-2"
                  : "grid-cols-1"
              }`}
            >
              {livnProductInfo.inclusions?.items?.length ? (
                <div>
                  <h2 className="text-base font-semibold mb-2">Included</h2>
                  <ul className="space-y-1">
                    {livnProductInfo.inclusions.items.map((item, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-600">
                        <span className="text-green-500 flex-shrink-0">✓</span>
                        <span>{item.content}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {livnProductInfo.exclusions?.items?.length ? (
                <div>
                  <h2 className="text-base font-semibold mb-2">Not Included</h2>
                  <ul className="space-y-1">
                    {livnProductInfo.exclusions.items.map((item, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-600">
                        <span className="text-red-400 flex-shrink-0">✗</span>
                        <span>{item.content}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* ── Notes ── */}
          {(livnProductInfo.pickupNotes || livnProductInfo.dropoffNotes || livnProductInfo.redemptionNotes) && (
            <div className="mb-5 space-y-2">
              {livnProductInfo.pickupNotes && (
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                  <span className="font-semibold">Pickup: </span>
                  {livnProductInfo.pickupNotes}
                </div>
              )}
              {livnProductInfo.dropoffNotes && (
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                  <span className="font-semibold">Dropoff: </span>
                  {livnProductInfo.dropoffNotes}
                </div>
              )}
              {livnProductInfo.redemptionNotes && (
                <div className="p-3 bg-green-50 rounded-lg text-sm text-green-800">
                  <span className="font-semibold">Redemption: </span>
                  {livnProductInfo.redemptionNotes}
                </div>
              )}
            </div>
          )}

          {livnProductInfo.specialNotes && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h2 className="text-sm font-semibold text-amber-800 mb-1">Important Information</h2>
              <p className="text-sm text-amber-700 whitespace-pre-line">{livnProductInfo.specialNotes}</p>
            </div>
          )}

          {/* ══════════════════════════════════
               BOOKING SECTION
          ══════════════════════════════════ */}
          {bookingConfirmed ? (
            <div className="flex flex-col gap-4">
              <div className="p-6 bg-green-50 border border-green-200 rounded-xl text-center">
                <div className="text-4xl mb-3">✓</div>
                <h2 className="text-xl font-bold text-green-700 mb-1">Booking Confirmed!</h2>
                {livnFlow?.livnReference && (
                  <p className="text-sm text-green-600 mb-1">
                    Reference: <strong>{livnFlow.livnReference}</strong>
                  </p>
                )}
                <p className="text-green-600 text-sm">Your booking has been successfully completed.</p>
                {paymentAuthorized && paymentTotal > 0 && (
                  <p className="text-xs text-green-600 mt-2">
                    Paid by card: {paymentTotal} {paymentCurrency}
                  </p>
                )}
              </div>

              {loadingLivnBookings ? (
                <div className="flex justify-center py-4">
                  <PreLoading size="small" />
                </div>
              ) : (
                <LivnBookingTickets
                  bookings={livnBookings ?? []}
                  markupPerc={markupPerc}
                  tourName={livnProductInfo.name}
                  fallbackTravelDate={selectedDate || undefined}
                  fallbackTimeLabel={snapshotTimeLabel}
                />
              )}

              {(livnBookings ?? []).length === 0 && !loadingLivnBookings && passengerSnapshot.length > 0 && (
                <div className="border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
                  <p className="font-medium text-gray-800 mb-1">Travellers</p>
                  {passengerSnapshot.map((name, i) => (
                    <p key={i}>{name}</p>
                  ))}
                  <p className="text-xs text-gray-500 mt-2">
                    Your tickets are being issued and will appear in your reservations shortly.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl p-6">
              {showPayment ? (
                <LivnPaymentStep
                  productName={livnProductInfo.name}
                  travelDate={selectedDate ? moment(selectedDate).format("ddd, MMM D, YYYY") : undefined}
                  timeLabel={snapshotTimeLabel}
                  paxCount={paxCount}
                  lines={paymentLines}
                  total={paymentTotal}
                  currency={paymentCurrency}
                  cancellationPolicy={priceData?.cancellationPolicy?.text}
                  generalTerms={priceData?.generalTerms}
                  localFees={priceData?.localFees}
                  submitting={loadingLivnFlow}
                  error={bookingError || undefined}
                  onBack={() => setShowPayment(false)}
                  onPay={handlePay}
                />
              ) : (
                <>
                  {/* Step header */}
                  {activeStep && (
                    <div className="mb-5 flex items-start justify-between gap-4">
                      <div>
                        {roadmapLength > 0 && activeStep.sequenceNumber && (
                          <span className="text-xs text-gray-400 uppercase tracking-wide font-medium">
                            Step {activeStep.sequenceNumber} of {roadmapLength}
                          </span>
                        )}
                        <h3 className="text-base font-semibold text-gray-800 capitalize">{stepLabel(activeStep)}</h3>
                        {activeStep.caption && <p className="text-xs text-gray-500 mt-0.5">{activeStep.caption}</p>}
                      </div>
                      {timeLeft !== null && stepExpiry && (
                        <div
                          className={`text-sm font-mono font-bold px-3 py-1.5 rounded-lg flex-shrink-0 text-right ${
                            timeLeft < 60
                              ? "bg-red-100 text-red-600"
                              : timeLeft < 120
                              ? "bg-amber-100 text-amber-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          <div>⏱ {formatTimeLeft(timeLeft)}</div>
                          <div className="text-xs font-normal opacity-75">
                            Expires{" "}
                            {new Date(stepExpiry).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <h2 className="text-lg font-semibold mb-4">Select Date &amp; Tickets</h2>

                  {/* ── Currency — fixed for the whole checkout, so it is chosen before the flow starts ── */}
                  {currencies.length > 1 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                      <div className="flex flex-wrap gap-2">
                        {currencies.map(c => (
                          <button
                            key={c}
                            className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                              activeCurrency === c
                                ? "bg-primary-color text-white border-primary-color"
                                : "bg-white text-gray-700 border-gray-300 hover:border-primary-color"
                            } ${selectedDate ? "opacity-60 cursor-not-allowed" : ""}`}
                            onClick={() => setSelectedCurrency(c)}
                            disabled={Boolean(selectedDate)}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                      {selectedDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          The currency is fixed once checkout starts. Pick a different date to change it.
                        </p>
                      )}
                    </div>
                  )}

                  {/* ── Date picker ── */}
                  {loadingLivnDepartures ? (
                    <div className="flex justify-center py-4">
                      <PreLoading size="small" />
                    </div>
                  ) : livnDepartures.length === 0 ? (
                    <p className="text-gray-500 text-sm mb-4">No available dates found.</p>
                  ) : (
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Available Dates</label>
                      <div className="overflow-x-auto pb-1">
                        <div className="flex gap-2" style={{ width: "max-content" }}>
                          {livnDepartures.map(dep => {
                            const isChosen = (selectedDate || pendingDate) === dep.date;
                            const depPrice = priceInCurrency(dep.fromPrices);
                            return (
                              <button
                                key={dep.date}
                                className={`px-3 py-2 rounded-lg border text-sm transition-colors flex-shrink-0 ${
                                  isChosen
                                    ? "bg-primary-color text-white border-primary-color"
                                    : "bg-white text-gray-700 border-gray-300 hover:border-primary-color"
                                }`}
                                onClick={() => handleDateClick(dep.date)}
                              >
                                <div>{moment(dep.date).format("MMM D")}</div>
                                {depPrice &&
                                  (() => {
                                    const displayed = displayLivnPrice(depPrice, livnProductInfo);
                                    return (
                                      <div className="text-xs opacity-80">
                                        {displayed.amount} {displayed.currency}
                                      </div>
                                    );
                                  })()}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Date confirmation — the flow (and its inventory hold) starts only on confirm ── */}
                  {pendingDate && !selectedDate && (
                    <div className="mb-5 p-4 border border-primary-color/40 bg-primary-color/5 rounded-lg">
                      <p className="text-sm font-medium text-gray-800">
                        Continue with {moment(pendingDate).format("dddd, MMMM D, YYYY")}?
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        We'll check live availability and hold your place while you complete the booking
                        {currencies.length > 1 ? `, priced in ${activeCurrency}` : ""}. You can still change the date
                        before paying.
                      </p>
                      <div className="flex gap-3 mt-3">
                        <button
                          className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-white"
                          onClick={() => setPendingDate("")}
                          disabled={loadingLivnFlow}
                        >
                          Change date
                        </button>
                        <button
                          className="flex-1 py-2.5 rounded-lg bg-primary-color text-white text-sm font-medium hover:bg-primary-color/90 disabled:opacity-50"
                          onClick={handleConfirmDate}
                          disabled={loadingLivnFlow}
                        >
                          {loadingLivnFlow ? <PreLoading size="small" color="white" /> : "Confirm date & continue"}
                        </button>
                      </div>
                    </div>
                  )}

                  {bookingNotice && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                      {bookingNotice}
                    </div>
                  )}

                  {selectedDate &&
                    (loadingLivnFlow && !activeStep ? (
                      <div className="flex justify-center py-8">
                        <PreLoading size="small" />
                      </div>
                    ) : activeStep ? (
                      <div className="space-y-5">
                        {/* ── Base Variant selector ── */}
                        {baseVariants.length > 1 ? (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Option</label>
                            <div className="flex flex-col gap-2">
                              {baseVariants.map((v, i) => (
                                <button
                                  key={i}
                                  className={`text-left px-4 py-3 rounded-lg border transition-colors text-sm ${
                                    selectedVariantIdx === i
                                      ? "border-primary-color bg-primary-color/5 font-medium text-gray-900"
                                      : "border-gray-200 text-gray-700 hover:border-gray-300"
                                  }`}
                                  onClick={() => {
                                    invalidateQuote();
                                    setSelectedVariantIdx(i);
                                    setSelectedTimeSlotIdx(0);
                                    setFareSelections({});
                                    setAddOnSelections({});
                                  }}
                                >
                                  {v.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          selectedVariant?.name && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Option</label>
                              <div className="px-4 py-3 rounded-lg border border-primary-color bg-primary-color/5 text-sm font-medium text-gray-900">
                                {selectedVariant.name}
                              </div>
                            </div>
                          )
                        )}

                        {/* ── Timeslot selector — a single available slot is still shown, so the
                               customer always sees the time they are booking ── */}
                        {timeSlots.length > 1 ? (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Time</label>
                            <div className="flex flex-wrap gap-2">
                              {timeSlots.map((ts, i) => (
                                <button
                                  key={i}
                                  className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                                    selectedTimeSlotIdx === i
                                      ? "bg-primary-color text-white border-primary-color"
                                      : "bg-white text-gray-700 border-gray-300 hover:border-primary-color"
                                  } ${ts.available === false ? "opacity-40 cursor-not-allowed" : ""}`}
                                  onClick={() => {
                                    invalidateQuote();
                                    setSelectedTimeSlotIdx(i);
                                    setFareSelections({});
                                    setAddOnSelections({});
                                  }}
                                  disabled={ts.available === false}
                                >
                                  <div className="font-medium">{timeRange(ts.timeStart, ts.timeEnd) ?? ts.name}</div>
                                  <div className="text-xs opacity-80">
                                    {[
                                      ts.duration ? formatDuration(ts.duration) : undefined,
                                      ts.available === false ? "Sold out" : undefined,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </div>
                                </button>
                              ))}
                            </div>
                            {selectedTimeSlot?.specialNotes && (
                              <p className="text-xs text-amber-600 mt-1.5">{selectedTimeSlot.specialNotes}</p>
                            )}
                          </div>
                        ) : (
                          selectedTimeSlot && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                              <div className="px-4 py-3 rounded-lg border border-gray-200 bg-gray-50">
                                <p className="text-sm font-medium text-gray-800">
                                  {timeRange(selectedTimeSlot.timeStart, selectedTimeSlot.timeEnd) ??
                                    selectedTimeSlot.name ??
                                    "Time confirmed by the supplier"}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {[
                                    selectedTimeSlot.duration ? formatDuration(selectedTimeSlot.duration) : undefined,
                                    "Only one departure time is available on this date",
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </p>
                                {selectedTimeSlot.specialNotes && (
                                  <p className="text-xs text-amber-600 mt-1">{selectedTimeSlot.specialNotes}</p>
                                )}
                              </div>
                            </div>
                          )
                        )}

                        {/* ── Fares ── */}
                        {currentFares.length > 0 && (
                          <div>
                            <div className="flex items-baseline justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">Select Tickets</label>
                              {declaredPaxCount !== undefined && (
                                <span className={`text-xs ${paxCountMismatch ? "text-red-500 font-medium" : "text-gray-400"}`}>
                                  {totalPax} of {declaredPaxCount} travellers selected
                                </span>
                              )}
                            </div>
                            <div className="space-y-2">
                              {currentFares.map(fare => {
                                const farePrice = displayLivnPrice(fare.price, livnProductInfo);
                                const fareMax = effectiveCap(fare.unitsMax, fare.unitsAvailable);
                                const soldOut = fare.unitsAvailable === 0;
                                const qty = fareSelections[fare.uuid] ?? 0;
                                return (
                                  <div key={fare.uuid}>
                                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                      <div className="flex-1 min-w-0 mr-3">
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                          <span className="font-medium text-gray-800 text-sm">{fare.name}</span>
                                          {(fare.ageMin !== undefined || fare.ageMax !== undefined) && (
                                            <span className="text-xs text-gray-400">
                                              {fare.ageMin !== undefined ? fare.ageMin : ""}
                                              {fare.ageMin !== undefined && fare.ageMax !== undefined
                                                ? `–${fare.ageMax}`
                                                : fare.ageMax !== undefined
                                                ? `≤${fare.ageMax}`
                                                : "+"}
                                              {" yrs"}
                                            </span>
                                          )}
                                          {fare.paxMin !== undefined && fare.paxMin > 1 && (
                                            <span className="text-xs text-gray-400">covers {fare.paxMin} travellers</span>
                                          )}
                                          {fare.unitsMin > 1 && <span className="text-xs text-gray-400">min {fare.unitsMin}</span>}
                                          {fareMax !== undefined && <span className="text-xs text-gray-400">max {fareMax}</span>}
                                          {fare.unitsMultipleOf && fare.unitsMultipleOf > 1 && (
                                            <span className="text-xs text-gray-400">in multiples of {fare.unitsMultipleOf}</span>
                                          )}
                                          {fare.unitsAvailable !== undefined && fare.unitsAvailable > 0 && fare.unitsAvailable < 10 && (
                                            <span className="text-xs text-red-500 font-medium">
                                              Only {fare.unitsAvailable} left
                                            </span>
                                          )}
                                          {soldOut && <span className="text-xs text-red-500 font-medium">Sold out</span>}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-x-2 mt-0.5">
                                          <span className="text-sm text-primary-color font-semibold">
                                            {farePrice.amount} {farePrice.currency}
                                            {fare.paxMin && fare.paxMin > 1 ? ` / ${fare.paxMin} pax` : " / person"}
                                          </span>
                                        </div>
                                        {fare.otherCharges && <p className="text-xs text-amber-600 mt-0.5">{fare.otherCharges}</p>}
                                        {fare.specialNotes && <p className="text-xs text-gray-500 mt-0.5">{fare.specialNotes}</p>}
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <button
                                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-40"
                                          onClick={() => handleFareChange(fare.uuid, -1, fare)}
                                          disabled={qty === 0}
                                          aria-label={`Remove ${fare.name}`}
                                        >
                                          <span className="text-lg leading-none">−</span>
                                        </button>
                                        <span className="w-6 text-center font-medium text-sm">{qty}</span>
                                        <button
                                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-40"
                                          onClick={() => handleFareChange(fare.uuid, 1, fare)}
                                          disabled={soldOut || (fareMax !== undefined && qty >= fareMax)}
                                          aria-label={`Add ${fare.name}`}
                                        >
                                          <span className="text-lg leading-none">+</span>
                                        </button>
                                      </div>
                                    </div>
                                    {/* Fare-level add-ons — offered once this fare is in the basket */}
                                    {(fare.addOns ?? []).length > 0 && qty > 0 && (
                                      <div className="ml-3 mt-1 space-y-1">
                                        {fare.addOns!.map(ao => renderAddOnRow(ao, fare.uuid))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {paxCountMismatch && (
                              <p className="text-xs text-red-500 mt-2">
                                {totalPax < declaredPaxCount!
                                  ? `Please select ${declaredPaxCount! - totalPax} more traveller${
                                      declaredPaxCount! - totalPax === 1 ? "" : "s"
                                    } to match the passenger count you entered.`
                                  : `That is ${totalPax - declaredPaxCount!} more than the passenger count you entered. Please remove some tickets.`}
                              </p>
                            )}
                          </div>
                        )}

                        {/* ── Timeslot-level add-ons ── */}
                        {timeSlotAddOns.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Optional Extras</label>
                            <div className="space-y-2">{timeSlotAddOns.map(ao => renderAddOnRow(ao))}</div>
                          </div>
                        )}

                        {/* ── Variant-level add-ons ── */}
                        {variantAddOns.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Add-ons</label>
                            <div className="space-y-2">{variantAddOns.map(ao => renderAddOnRow(ao))}</div>
                          </div>
                        )}

                        {/* ── Questions, grouped exactly as Livn groups them so each answer is
                               clearly attributed to the customer or a named participant ── */}
                        {questionGroups.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {questionGroups.some(g => /participant/i.test(g.caption ?? ""))
                                ? "Traveller & Contact Details"
                                : "Additional Information"}
                            </label>
                            <p className="text-xs text-gray-500 mb-3">
                              The supplier needs these details to issue your tickets. Enter each traveller's name exactly
                              as it appears on their photo ID — names cannot be changed after booking. Fields marked *
                              are required.
                            </p>
                            <div className="space-y-3">
                              {questionGroups.map((group, gi) => (
                                <div key={gi} className="border border-gray-100 rounded-lg p-3 bg-gray-50/50">
                                  <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                                    {group.caption || "Details"}
                                  </p>
                                  <div className="space-y-2">{group.questions.map(q => renderQuestion(q))}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ── Price summary ── */}
                        {livePrice && (
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <h3 className="font-medium text-gray-700 mb-2 text-sm">Price Summary</h3>
                            {paymentLines.map((line, i) => (
                              <div key={i} className="flex justify-between text-sm text-gray-600 mb-1">
                                <span>
                                  {line.title} × {line.quantity}
                                </span>
                                <span>
                                  {line.amount} {line.currency}
                                </span>
                              </div>
                            ))}
                            <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-semibold text-sm">
                              <span>Total</span>
                              <span>
                                {paymentTotal} {paymentCurrency}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* ── Final Quote details (cancellation policy, T&C, local fees) ── */}
                        {activeStep?.finalQuote &&
                          (() => {
                            const fq = activeStep.finalQuote!;
                            return (
                              <div className="flex flex-col gap-3">
                                {fq.localFees && (
                                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                                    <span className="font-medium">Payable locally: </span>
                                    {fq.localFees}
                                  </div>
                                )}
                                {fq.cancellationPolicy?.text && (
                                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                                    <p className="font-medium mb-1">Cancellation Policy</p>
                                    <p>{fq.cancellationPolicy.text}</p>
                                  </div>
                                )}
                                {fq.generalTerms && (
                                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                                    <p className="font-medium mb-1">Terms &amp; Conditions</p>
                                    <p>{fq.generalTerms}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                        {/* ── Errors ── */}
                        {bookingError && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            {bookingError}
                          </div>
                        )}
                        {showAllAnswerErrors && !answersValid && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            Please correct the highlighted fields before continuing.
                          </div>
                        )}

                        {/* ── Action buttons ── */}
                        {activeStep?.error?.goBackToRetry ? (
                          <button
                            className="w-full py-3 rounded-lg border-2 border-primary-color text-primary-color font-medium hover:bg-primary-color/5 transition-colors disabled:opacity-50"
                            onClick={handleGoBack}
                            disabled={loadingLivnFlow}
                          >
                            {loadingLivnFlow ? <PreLoading size="small" /> : "← Go Back"}
                          </button>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-3">
                              {hasFares && !livnPricePreview && (
                                <button
                                  className="flex-1 py-3 rounded-lg border-2 border-primary-color text-primary-color font-medium hover:bg-primary-color/5 transition-colors disabled:opacity-50"
                                  onClick={handleCheckPrice}
                                  disabled={loadingLivnPricePreview || !selectionComplete}
                                >
                                  {loadingLivnPricePreview ? <PreLoading size="small" /> : "Check Price"}
                                </button>
                              )}
                              {(livnPricePreview || !hasFares) &&
                                (isLastStep ? (
                                  <button
                                    className="flex-1 py-3 rounded-lg bg-primary-color text-white font-medium hover:bg-primary-color/90 transition-colors disabled:opacity-50"
                                    onClick={handleProceedToPayment}
                                    disabled={loadingLivnFlow || !canSubmit}
                                  >
                                    Continue to Payment
                                  </button>
                                ) : (
                                  <button
                                    className="flex-1 py-3 rounded-lg bg-primary-color text-white font-medium hover:bg-primary-color/90 transition-colors disabled:opacity-50"
                                    onClick={handleContinue}
                                    disabled={loadingLivnFlow || !selectionComplete}
                                  >
                                    {loadingLivnFlow ? <PreLoading size="small" color="white" /> : "Continue"}
                                  </button>
                                ))}
                            </div>
                            {isLastStep && (
                              <p className="text-xs text-gray-500 text-center">
                                You'll review the total and pay on the next step. Nothing is charged until you confirm.
                              </p>
                            )}
                            {hasFares && !hasTicketsSelected && (
                              <p className="text-xs text-gray-500 text-center">Select at least one ticket to continue.</p>
                            )}
                          </div>
                        )}
                      </div>
                    ) : null)}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LivnTourInfo;
